import { useState, useEffect, useMemo, useCallback } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar, { MobileSidebar, MobileTopBar } from './components/Sidebar';
import Dashboard from './components/Dashboard';
import GeneralStatistics from './components/GeneralStatistics';
import CallTypesOutcomes from './components/callTypes/CallTypesOutcomes';
import EmployeeAnalytics from './components/EmployeeAnalytics';
import CallDetails from './components/CallDetails';
import MissedCalls from './components/MissedCalls';
import Clients from './components/Clients';
import Complaints from './components/Complaints';
import Integrations from './components/Integrations';
import NumberFilters from './components/NumberFilters';
import Settings from './components/Settings';
import Support from './components/Support';
import Login from './components/Login';
import SessionSplash from './components/SessionSplash';
import UsageEventsBridge from './components/UsageEventsBridge';
import ExportJobsPanel, { ExportJobsLauncher } from './components/exports/ExportJobsPanel';
import { ExportJobsProvider } from './components/exports/ExportJobsContext';
import { Toaster } from './components/ui/toaster';
import { TimeZoneProvider } from './contexts/TimeZoneContext';
import { FeaturesProvider } from './contexts/FeaturesContext';
import { PanelSettingsProvider } from './contexts/PanelSettingsContext';
import { CallCardFieldsProvider } from './contexts/CallCardFieldsContext';
import { clearCachedPanelSettings } from './contexts/panelSettingsState';
import { fetchCurrentUser, logoutSession } from './api';
import { APP_TAB_IDS } from './config/navigation';
import { effectiveCallType, hasDirectionFilter, hasPeriodBar } from './config/sectionFilters';
import { persistSidebarCollapsed, readSidebarCollapsed } from './config/sidebarState';
import { useIsDesktop } from './hooks/useMediaQuery';
import { getDefaultDateRange } from './utils/defaultDateRange';
import {
  buildCallJournalUrl,
  readLinkedCallType,
  readLinkedDateRange,
  readLinkedJournalFilters
} from './components/callJournalUrl';
import {
  ANONYMOUS_SESSION,
  AUTH_STATUS,
  CHECKING_SESSION,
  authenticatedSession,
  clearLegacyAuthStorage,
  resolveSession,
  sessionRetryDelayMs
} from './lib/authSession';
import { startUsageCapture } from './lib/usageEventCapture';
import { flushUsageEvents, resetUsageEvents, trackSectionView } from './lib/usageEvents';
import { toast } from './hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') ?? '';

const API = `${BACKEND_URL}/api`;

const getInitialDateRange = () => getDefaultDateRange();

const getAllowedTabs = (authenticatedUser) =>
  Array.isArray(authenticatedUser?.permissions?.allowedTabs)
    ? authenticatedUser.permissions.allowedTabs
    : [];

const canUserAccess = (authenticatedUser, tabId) =>
  authenticatedUser?.role === 'admin' || getAllowedTabs(authenticatedUser).includes(tabId);

const getDefaultSectionForUser = (authenticatedUser) =>
  APP_TAB_IDS.find((tabId) => canUserAccess(authenticatedUser, tabId)) ?? 'dashboard';

const getPreferredSectionForUser = (authenticatedUser, requestedSection) =>
  requestedSection && canUserAccess(authenticatedUser, requestedSection)
    ? requestedSection
    : getDefaultSectionForUser(authenticatedUser);

const CallCenterApp = () => {
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(() => {
    const section = searchParams.get('section');
    return section || 'dashboard';
  });
  const [initialCallId, setInitialCallId] = useState(() => searchParams.get('callId'));
  const [callJournalFilters, setCallJournalFilters] = useState(() => readLinkedJournalFilters(searchParams));
  // Вошёл или нет, знает только сервер: пока он не ответил, не рисуется ни дашборд, ни форма входа.
  const [session, setSession] = useState(CHECKING_SESSION);
  const isAuthenticated = session.status === AUTH_STATUS.AUTHENTICATED;
  const user = session.user;
  const [dateRange, setDateRange] = useState(() => readLinkedDateRange(searchParams) ?? getInitialDateRange());
  const [callType, setCallType] = useState(() => readLinkedCallType(searchParams) ?? 'all');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readSidebarCollapsed);
  const isDesktop = useIsDesktop();
  const allowedTabs = useMemo(() => getAllowedTabs(user), [user]);
  const canAccess = useCallback(
    (tabId) => user?.role === 'admin' || allowedTabs.includes(tabId),
    [allowedTabs, user?.role]
  );
  const defaultSection = useMemo(() => getDefaultSectionForUser(user), [user]);

  useEffect(() => startUsageCapture(), []);

  // Недоступный сервер (идёт обновление стенда) — не повод показывать форму входа: проверяем, пока не ответит.
  useEffect(() => {
    let isCancelled = false;
    let retryTimer = null;

    const check = async (attempt) => {
      const nextSession = await resolveSession(fetchCurrentUser);
      if (isCancelled) {
        return;
      }

      setSession(nextSession);
      if (nextSession.status === AUTH_STATUS.UNAVAILABLE) {
        retryTimer = setTimeout(() => check(attempt + 1), sessionRetryDelayMs(attempt));
      }
    };

    clearLegacyAuthStorage();
    check(0);

    return () => {
      isCancelled = true;
      clearTimeout(retryTimer);
    };
  }, []);

  // URL при смене раздела не меняется, поэтому просмотр раздела сообщаем сами.
  useEffect(() => {
    if (isAuthenticated) {
      trackSectionView(activeSection);
    }
  }, [activeSection, isAuthenticated]);

  const checkBackendHealth = async () => {
    const backendBase = BACKEND_URL?.replace(/\/$/, '');

    if (!backendBase) {
      console.warn('Backend URL is not configured; skipping health check.');
      return;
    }

    try {
      const response = await axios.get(`${backendBase}/healthz`);
      console.log('Backend health:', response.data?.status ?? 'ok');
    } catch (e) {
      console.error('Backend health check failed', e);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      checkBackendHealth();
    }
  }, [isAuthenticated]);

  // Окно расширили до десктопа с открытым меню — панель закрываем: на десктопе её место
  // занимает сайдбар, и оставшаяся поверх него шторка выглядела бы зависшей.
  useEffect(() => {
    if (isDesktop) {
      setIsMobileNavOpen(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    persistSidebarCollapsed(isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  const toggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed((collapsed) => !collapsed);
  }, []);

  useEffect(() => {
    const onUnauthorized = () => endSessionLocally();
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user && !canAccess(activeSection)) {
      setActiveSection(defaultSection);
    }
  }, [activeSection, canAccess, defaultSection, isAuthenticated, user]);

  const handleLoginSuccess = (authenticatedUser) => {
    setSession(authenticatedSession(authenticatedUser));
    setActiveSection(getPreferredSectionForUser(authenticatedUser, searchParams.get('section')));
  };

  const endSessionLocally = () => {
    resetUsageEvents();
    // Настройки панелей тенант-скоупные: оставленный кэш показал бы следующему пользователю
    // чужие названия до ответа сервера.
    clearCachedPanelSettings();
    setSession(ANONYMOUS_SESSION);
    setActiveSection('dashboard');
  };

  // Метрики досылаем по возможности: последняя пачка может не успеть раньше отзыва сессии и потеряться,
  // телеметрия этого стоит. Убрать HttpOnly-cookie может только сервер, поэтому
  // при сбое пользователь остаётся внутри и видит это: показать форму входа значило бы соврать, что он
  // вышел, и на общем компьютере следующий человек после перезагрузки оказался бы в чужой сессии.
  const handleLogout = async () => {
    flushUsageEvents();
    try {
      await logoutSession();
    } catch {
      toast({
        title: 'Не удалось выйти',
        description: 'Сервер недоступен, сессия не завершена. Повторите через минуту.',
        variant: 'destructive'
      });
      return;
    }
    endSessionLocally();
  };

  const handleInitialCallHandled = useCallback(() => {
    setInitialCallId(null);
  }, []);

  const handleInitialFiltersHandled = useCallback(() => {
    setCallJournalFilters(null);
  }, []);

  // Направление, которым считает открытый раздел: где селекта нет, — все звонки. Сам выбор
  // остаётся в callType и вернётся, когда пользователь придёт обратно (config/sectionFilters).
  const sectionCallType = effectiveCallType(activeSection, callType);

  // Журнал открывается в новой вкладке: раздел, с которого пришли, не теряет выбор и графики.
  // Если браузер заблокировал вкладку, остаётся переход здесь же.
  // Payload описывает состояние drill-down целиком: отсутствующий ключ означает сброс,
  // а не сохранение прежнего значения. Иначе два перехода подряд накапливали пересечение
  // срезов, которого пользователь не выбирал.
  const openCallJournal = useCallback((filters) => {
    const url = buildCallJournalUrl(window.location.href, filters, { dateRange, callType: sectionCallType });
    const openedTab = window.open(url, '_blank');
    if (openedTab) {
      openedTab.opener = null;
      return;
    }

    const { callId, ...rest } = filters ?? {};

    if (callId) {
      setInitialCallId(callId);
    }

    setCallJournalFilters(Object.keys(rest).length > 0 ? rest : null);
    setActiveSection('calls');
  }, [dateRange, sectionCallType]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <GeneralStatistics dateRange={dateRange} callType={sectionCallType} onOpenCallJournal={openCallJournal} />;
      case 'employees':
        return (
          <EmployeeAnalytics dateRange={dateRange} callType={sectionCallType} onOpenCallJournal={openCallJournal} />
        );
      case 'calls':
        return (
          <CallDetails
            dateRange={dateRange}
            callType={sectionCallType}
            initialCallId={initialCallId}
            onInitialCallHandled={handleInitialCallHandled}
            initialFilters={callJournalFilters}
            onInitialFiltersHandled={handleInitialFiltersHandled}
          />
        );
      case 'call-paths':
        return <CallTypesOutcomes dateRange={dateRange} callType={sectionCallType} onOpenCallJournal={openCallJournal} />;
      case 'missed':
        return <MissedCalls dateRange={dateRange} />;
      case 'clients':
        return canAccess('clients') ? <Clients dateRange={dateRange} /> : <GeneralStatistics dateRange={dateRange} callType={sectionCallType} />;
      case 'complaints':
        return canAccess('complaints')
          ? <Complaints dateRange={dateRange} callType={sectionCallType} />
          : <GeneralStatistics dateRange={dateRange} callType={sectionCallType} />;
      case 'integrations':
        return <Integrations dateRange={dateRange} user={user} />;
      case 'number-filters':
        return canAccess('number-filters') ? <NumberFilters /> : <GeneralStatistics dateRange={dateRange} callType={sectionCallType} />;
      case 'settings':
        return <Settings user={user} />;
      case 'help':
        return <Support user={user} />;
      default:
        return <GeneralStatistics dateRange={dateRange} callType={sectionCallType} />;
    }
  };

  if (session.status === AUTH_STATUS.CHECKING || session.status === AUTH_STATUS.UNAVAILABLE) {
    return <SessionSplash unavailable={session.status === AUTH_STATUS.UNAVAILABLE} />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <Login apiBaseUrl={API} onSuccess={handleLoginSuccess} />
        </div>
        <Toaster />
      </>
    );
  }

  // Провайдеры живут внутри аутентифицированной ветки: их запросы требуют входа, а при логауте всё
  // поддерево размонтируется и значения сбрасываются сами. Заодно часовой пояс загружается заново после
  // каждого входа: снаружи ветки он запрашивался один раз, ещё на экране входа, и получал 401.
  return (
    <TimeZoneProvider>
    <FeaturesProvider isAuthenticated>
    <UsageEventsBridge />
    <PanelSettingsProvider isAuthenticated isAdmin={user?.role === 'admin'}>
    <CallCardFieldsProvider isAuthenticated>
    <ExportJobsProvider>
      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
        <Sidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          onLogout={handleLogout}
          user={user}
          canAccess={canAccess}
          collapsed={isSidebarCollapsed}
          onToggleCollapsed={toggleSidebarCollapsed}
        />

        <MobileTopBar activeSection={activeSection} onOpenMenu={() => setIsMobileNavOpen(true)} />
        <MobileSidebar
          open={isMobileNavOpen}
          onOpenChange={setIsMobileNavOpen}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          onLogout={handleLogout}
          user={user}
          canAccess={canAccess}
        />

        {/* `min-w-0`: без него широкая таблица распирает flex-1 и уводит вбок всю страницу,
            а не прокручивается внутри своей карточки. `pt-14` — место под фиксированную
            мобильную панель. */}
        {/* `data-section` — единственный машиночитаемый ответ на вопрос «какой раздел
            показан»: маршрут в приложении один, и URL после клика по сайдбару
            не меняется. На нём стоят e2e-спеки (CallCenter.Web.E2E). */}
        <main
          data-section={activeSection}
          className="flex-1 min-w-0 min-h-screen overflow-y-auto bg-gray-50 pt-14 dark:bg-gray-900 lg:pt-0"
        >
          <div className="flex flex-col">
            {/* Срез — период и направление — общий для разделов. Панели нет там, где нет
                временного среза, а селекта направления — там, где раздел его не использует
                (config/sectionFilters). */}
            {hasPeriodBar(activeSection, isDesktop) && (
              <Dashboard
                activeSection={activeSection}
                onSectionChange={setActiveSection}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                callType={sectionCallType}
                showCallType={hasDirectionFilter(activeSection)}
                onCallTypeChange={setCallType}
              />
            )}

            {/* Нижний отступ на телефоне больше: плавающая кнопка выгрузок иначе накрывает
                пагинацию последней карточки. */}
            <div className="px-3 pb-20 sm:px-6 sm:pb-8">
              {renderActiveSection()}
            </div>
          </div>
        </main>
      </div>
      <ExportJobsLauncher />
      <ExportJobsPanel />
      <Toaster />
    </ExportJobsProvider>
    </CallCardFieldsProvider>
    </PanelSettingsProvider>
    </FeaturesProvider>
    </TimeZoneProvider>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<CallCenterApp />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
